import { commonUtils } from '@/helpers/commonUtils'
import styles from './PptPage8.module.less'
import MarkdownRenderer from '@/components/MarkdownRenderer/MarkdownRenderer'
import { useTranslation } from 'react-i18next'
import { SliderModels } from '@/types/slider'
import PptPage, { PageHeader } from '../PptPage/PptPage'
import { useEffect, useRef, useState } from 'react'
import { useSize } from 'ahooks'

interface Props {
  project: ProjectModels.ProjectDetail
  no: number
  onSliderReady: (slider: SliderModels.Slider, no: number) => void
}


function PptPage8({ project, no, onSliderReady }: Props) {
  const { t } = useTranslation()
  const BOX_MAX_HEIGHT = 650
  const [ratio, setRatio] = useState<number>(1)
  const [ratioFinished, setRatioFinished] = useState<boolean>(false)
  const [basicSlider, setBasicSlider] = useState<SliderModels.Slider>()
  const [elements, setElements] = useState<SliderModels.PPTElement[]>([])
  const [pageHeaderElements, setPageHeaderElements] = useState<SliderModels.PPTElement[]>([])
  const [pageRect, setPageRect] = useState<DOMRect>()

  const lightRef = useRef<HTMLImageElement>(null)
  const titleRef = useRef<HTMLSpanElement>(null)
  const summaryRef = useRef<HTMLDivElement>(null)
  const [summaryChildren, setSummaryChildren] = useState<HTMLCollection>()
  const placeholderRef = useRef<HTMLImageElement>(null)

  const summarySize = useSize(summaryRef.current)

  const onPageRender = (slider: SliderModels.Slider, pageRect: DOMRect) => {
    setBasicSlider(slider)
    setPageRect(pageRect)
  }
  const onPageHeaderRender = (elements: SliderModels.PPTElement[]) => {
    setPageHeaderElements(elements)
  }

  useEffect(() => {
    if (
      !summaryChildren ||
      !lightRef.current ||
      !titleRef.current ||
      !placeholderRef.current ||
      !summaryRef.current
    ) {
      return
    }
    const lightRect = lightRef.current.getBoundingClientRect()
    const titleRect = titleRef.current.getBoundingClientRect()
    const placeholderRect = placeholderRef.current.getBoundingClientRect()
    const summaryRect = summaryRef.current.getBoundingClientRect()
    // 20 是 border-radius
    const SHAPE_RATIO = 20 / BOX_MAX_HEIGHT
    // 因为各个方块的大小高度其实是一致的，所以这里统一设置
    const SHAPE_WIDTH = summaryRect.width
    const SHAPE_HEIGHT = summaryRect.height
    const SHAPE_HEIGHT_RATIO = SHAPE_HEIGHT * SHAPE_RATIO
    const SHAPE_WIDTH_DELTA = SHAPE_WIDTH - SHAPE_HEIGHT_RATIO
    const SHAPE_HEIGHT_DELTA = SHAPE_HEIGHT - SHAPE_HEIGHT_RATIO

    const elements: SliderModels.PPTElement[] = [
      {
        type: 'image',
        src: lightRef.current.src,
        x: lightRect.x - 10,
        y: lightRect.y,
        width: lightRect.width,
        height: lightRect.height,
      },
      {
        type: 'text',
        content: titleRef.current.outerHTML,
        x: titleRect.x + 10,
        y: titleRect.y - 60,
        width: titleRect.width,
        height: titleRect.height,
      },
      {
        type: 'shape',
        x: summaryRect.x - 10,
        y: summaryRect.y,
        width: summaryRect.width,
        height: summaryRect.height + summaryChildren.length * 30,
        viewBox: [summaryRect.width, summaryRect.height],
        path: `M ${SHAPE_HEIGHT_RATIO} 0 L ${SHAPE_WIDTH_DELTA} 0 Q ${SHAPE_WIDTH} 0 ${SHAPE_WIDTH} ${SHAPE_HEIGHT_RATIO} L ${SHAPE_WIDTH} ${SHAPE_HEIGHT_DELTA} Q ${SHAPE_WIDTH} ${SHAPE_HEIGHT} ${SHAPE_WIDTH_DELTA} ${SHAPE_HEIGHT} L ${SHAPE_HEIGHT_RATIO} ${SHAPE_HEIGHT} Q 0 ${SHAPE_HEIGHT} 0 ${SHAPE_HEIGHT_DELTA} L 0 ${SHAPE_HEIGHT_RATIO} Q 0 0 ${SHAPE_HEIGHT_RATIO} 0 Z`,
        fill: '#f0f0ff',
        fixedRatio: false,
        rotate: 0,
        pathFormula: SliderModels.ShapePathFormulasKeys.ROUND_RECT,
        keypoints: [SHAPE_RATIO],
        // NOTE: 这里注释掉是因为 pptxgenjs 不支持 gradient 的设置
        // gradient: {
        //   type: 'linear',
        //   rotate: 22,
        //   colors: [
        //     { pos: 0, color: '#F4F4FE' },
        //     { pos: 40, color: '#D9D8FF' },
        //     { pos: 100, color: '#F6F6FF' },
        //   ],
        // },
        outline: {
          width: 1,
          color: '#D5D4FC',
          style: 'solid',
        },
      },
      ...Array.from(summaryChildren).map((el) => {
        const rect = el.getBoundingClientRect()
        return {
          type: 'text',
          content: el.outerHTML.replace(/\n/g, ''),
          x: rect.x - 10,
          y: rect.y - 30,
          width: rect.width,
          height: rect.height,
        } as SliderModels.PPTTextElement
      }),
      {
        type: 'image',
        src: placeholderRef.current.src,
        x: placeholderRect.x,
        y: placeholderRect.y,
        width: placeholderRect.width,
        height: placeholderRect.height,
      },
    ]
    setElements(elements)
  }, [summaryChildren])

  useEffect(() => {
    if (!pageRect || !basicSlider || pageHeaderElements.length === 0 || elements.length === 0) {
      return
    }
    const _pageHeaderElements = pageHeaderElements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })
    const _elements = elements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })

    const slider: SliderModels.Slider = {
      ...basicSlider,
      elements: [..._pageHeaderElements, ...basicSlider.elements, ..._elements],
    }
    onSliderReady(slider, no)
  }, [pageRect, basicSlider, pageHeaderElements, elements])

  useEffect(() => {
    if (!ratioFinished || !summaryRef.current || !project) {
      return
    }
    const summaryElement = summaryRef.current
    const children = summaryElement.children
    for (const child of children) {
      const c = child as HTMLElement
      c.style.fontSize = `${24 * ratio}px`
      c.style.color = '#2A2C3F'
    }
    setSummaryChildren(children)
  }, [project, ratio, ratioFinished])

  // useEffect(() => {
  //   if (summaryRef.current) {
  //     const summaryHeight = summaryRef.current.getBoundingClientRect().height
  //     if (summaryHeight > BOX_MAX_HEIGHT) {
  //       const ratio = BOX_MAX_HEIGHT / summaryHeight
  //       setRatio(ratio)
  //     }
  //     setRatioFinished(true)
  //   }
  // }, [])

  useEffect(() => {
    if (summarySize) {
      const summaryHeight = summarySize.height
      if (summaryHeight > BOX_MAX_HEIGHT) {
        setRatio((ratio) => ratio - 0.1)
      } else {
        setRatioFinished(true)
      }
    }
  }, [summarySize])


  return (
    <PptPage
      bgSrc={commonUtils.getImagePath('PPTDownload/bg-6.jpg')}
      no={no}
      onPageRender={onPageRender}
    >
      <div className={styles.pptPage8}>
        <PageHeader
          project={project}
          text={'02.' + t('dashboard.projectDetailPage.primaryAnalysis')}
          onRender={onPageHeaderRender}
        />
        <div className={styles.content}>
          <div className={styles.contentBox}>
            <div className={styles.left}>
              <div className={styles.title}>
                <img
                  ref={lightRef}
                  style={{ width: 48, height: 48 }}
                  src={commonUtils.getImagePath('projectDetailPage/range-title-light.png')}
                  alt=''
                />
                <span
                  ref={titleRef}
                  style={{
                    fontSize: 36,
                    color: '#6D54FA',
                    fontWeight: 'bold',
                  }}
                >
                  {t('dashboard.projectDetailPage.storyboardView.summary')}
                </span>
              </div>
              <div className={styles.summary} ref={summaryRef}>
                <MarkdownRenderer
                  style={{ fontSize: 24 * ratio }}
                  markdownText={project.globalMetrics.reason.performance} />
              </div>
            </div>

            <div className={styles.right}>
              <img
                className={styles.img}
                style={{ width: 609, height: 609 }}
                ref={placeholderRef}
                src={commonUtils.getImagePath('PPTDownload/right-placeholder-1.png')}
              />
            </div>
          </div>
        </div>
      </div>
    </PptPage>
  )
}

export default PptPage8
